import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Facebook, Instagram, Youtube, Mail } from 'lucide-react';
import { useSiteConfig } from '../context/SiteConfigContext';

// Social icon mapping
const socialIcons = {
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  email: Mail
};

const Footer = () => {
  const [currentTheme, setCurrentTheme] = useState('dim');
  const { getLogo, branding, footer, event, getNavigationItems } = useSiteConfig();

  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setCurrentTheme(theme);
    };
    checkTheme();
    window.addEventListener('storage', checkTheme);
    const interval = setInterval(checkTheme, 500);
    return () => {
      window.removeEventListener('storage', checkTheme);
      clearInterval(interval);
    };
  }, []);

  // Select logo based on theme
  const logoSrc = getLogo(currentTheme);

  // Get visible social links
  const visibleSocialLinks = (footer?.social_links || []).filter(link => link.visible !== false);

  // Get nav items for footer links
  const navItems = getNavigationItems().slice(0, 4); // Show first 4 nav items

  return (
    <footer className="relative z-10 border-t border-border bg-muted/30">
      {/* Main Footer */}
      <div className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <img 
                  src={logoSrc}
                  alt={branding?.site_name || "Conference"}
                  className="h-10 w-auto object-contain"
                />
              </Link>
              <p className="text-muted-foreground mb-4 max-w-md">
                {footer?.about_text || branding?.tagline || "Welcome to our conference."}
              </p>
              
              {/* Social Links */}
              {visibleSocialLinks.length > 0 && (
                <div className="flex items-center gap-3 mt-4">
                  {visibleSocialLinks.map((social, index) => {
                    const IconComponent = socialIcons[social.platform] || Mail;
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-full bg-foreground/5 hover:bg-foreground/10 transition-colors"
                        aria-label={social.platform}
                      >
                        <IconComponent className="w-4 h-4" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Conference Links */}
            <div>
              <h4 className="font-playfair font-semibold mb-4">Conference</h4>
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link to={item.path} className="text-muted-foreground hover:text-foreground transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/signup" className="text-muted-foreground hover:text-foreground transition-colors">
                    Register
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              {footer?.copyright_text || `© ${new Date().getFullYear()} ${branding?.site_name || 'Conference'}. All rights reserved.`}
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">
                Terms of Service
              </Link>
              {event?.organizer_website && (
                <a href={event.organizer_website} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                  {event.organizer_name}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
